import type { CountryResult, RateLine } from "../../../../shared/contracts/query";

type Country = CountryResult["country"];

export type CountryCardProps = {
  result: CountryResult;
  expanded: boolean;
  onExpand: () => void;
};

const COUNTRY_META: Record<Country, {
  heading: string;
  codeLabel: string;
  baseCategory: RateLine["category"][];
  accentClass: string;
}> = {
  CN: {
    heading: "中国出口",
    codeLabel: "商品编码",
    baseCategory: ["export_duty", "provisional_export_duty"],
    accentClass: "country-card-cn",
  },
  US: {
    heading: "美国进口",
    codeLabel: "HTS",
    baseCategory: ["base_duty"],
    accentClass: "country-card-us",
  },
  CA: {
    heading: "加拿大进口",
    codeLabel: "Tariff",
    baseCategory: ["base_duty"],
    accentClass: "country-card-ca",
  },
};

const TRANSLATION_STATUS: Record<CountryResult["chineseExplanation"]["status"], string> = {
  machine: "机器翻译",
  human_reviewed: "人工审核",
  not_needed: "无需翻译",
};

function displayPercent(value: string): string {
  return value.endsWith("%") ? value : `${value}%`;
}

function legalNameRows(result: CountryResult): Array<{ label: string; text: string }> {
  if (result.country !== "CN") {
    const chinese = result.legalNames.find((name) => name.language.toLowerCase().startsWith("zh"));
    return [{ label: "法定品名（中文释义）", text: chinese?.text ?? result.chineseExplanation.text }];
  }
  const primary = result.legalNames[0];
  return primary ? [{ label: "法定品名", text: primary.text }] : [];
}

function headingContext(result: CountryResult) {
  if (result.country === "CN") return null;
  const heading = result.hierarchy.find((node) => node.codeDigits === 4);
  const name = heading?.legalNames.find((item) => item.language.toLowerCase().startsWith("zh"));
  return heading
    ? { chapter: heading.code.slice(0, 2), displayCode: heading.displayCode, text: name?.text ?? "中文释义暂未生成" }
    : null;
}

function rateStatus(rate: RateLine): string | null {
  if (rate.confirmed && rate.includedInConfirmedTotal) return "已确认";
  if (rate.confirmed && !rate.includedInConfirmedTotal) return "已确认 · 未计入";
  if (rate.category === "additional_duty" || rate.category === "trade_remedy") return "可能适用 · 未计入";
  if (rate.category === "tax" || rate.category === "fee") return "独立税费 · 未计入";
  return "人工复核 · 未计入";
}

function isBaseRate(result: CountryResult, rate: RateLine): boolean {
  return COUNTRY_META[result.country].baseCategory.includes(rate.category)
    && rate.confirmed;
}

function normalizedRateLabel(label: string): string {
  return label.normalize("NFKC").toLocaleLowerCase("en-US");
}

function identifiesUsGeneral(label: string): boolean {
  const normalized = normalizedRateLabel(label);
  return normalized.includes("general") || normalized.includes("一般") || normalized.includes("普通");
}

function identifiesCaMfn(label: string): boolean {
  const normalized = normalizedRateLabel(label);
  return normalized.includes("mfn")
    || normalized.includes("most favoured nation")
    || normalized.includes("most favored nation")
    || normalized.includes("最惠国");
}

function rateLabel(result: CountryResult, rate: RateLine, primary = false): string {
  if (result.country === "CN" && rate.category === "export_duty") return "出口关税率";
  if (result.country === "CN" && rate.category === "provisional_export_duty") return "出口暂定税率";
  if (primary && result.country === "US" && rate.category === "base_duty" && identifiesUsGeneral(rate.label)) return "General 基础关税";
  if (primary && result.country === "CA" && rate.category === "base_duty" && identifiesCaMfn(rate.label)) return "MFN 税率";
  return rate.label;
}

function rateLineKey(rate: RateLine, index: number): string {
  return `${rate.id}-${index}`;
}

function isTradeRemedyRate(rate: RateLine): boolean {
  return rate.category === "additional_duty" || rate.category === "trade_remedy";
}

export function CountryCard({ result, expanded, onExpand }: CountryCardProps) {
  const meta = COUNTRY_META[result.country];
  const detailId = `country-detail-${result.country}`;
  const primaryRate = result.rates.find((rate) => isBaseRate(result, rate) && rate.includedInConfirmedTotal)
    ?? result.rates.find((rate) => isBaseRate(result, rate));
  const remainingRates = primaryRate
    ? result.rates.filter((rate) => rate !== primaryRate)
    : result.rates;
  const ordinaryRates = remainingRates.filter((rate) => !isTradeRemedyRate(rate));
  const tradeRemedyRates = remainingRates.filter(isTradeRemedyRate);
  const simaCodes = result.country === "CA"
    ? [...new Set(result.measures
      .filter((measure) => measure.measureType.toLocaleLowerCase("en-US") === "sima")
      .map((measure) => measure.codeHint)
      .filter((code): code is string => Boolean(code)))]
    : [];
  const heading = headingContext(result);
  const nameRows = legalNameRows(result);
  const showExplanation = !nameRows.some((row) => row.text === result.chineseExplanation.text);
  const noChineseExportRate = result.country === "CN"
    && !result.rates.some((rate) => rate.category === "export_duty" || rate.category === "provisional_export_duty");

  return (
    <article className={`country-card ${meta.accentClass}`}>
      <div className="country-card-heading">
        <div>
          <h3>{meta.heading}</h3>
        </div>
        <span className="country-status">{result.status === "confirmed" ? "已确认命中" : result.status === "candidate" ? "候选命中" : result.status === "possible" ? "可能适用" : "人工复核"}</span>
      </div>

      <dl className="country-identification">
        <div>
          <dt>{meta.codeLabel}</dt>
          <dd className="country-code">{result.displayCode}</dd>
        </div>
        {heading ? (
          <div>
            <dt>章节与大类</dt>
            <dd className="country-detail country-legal-name">
              <strong>第{heading.chapter}章 · 税目 {heading.displayCode}</strong><br />
              {heading.text}
            </dd>
          </div>
        ) : null}
        {nameRows.map((row) => (
          <div key={`${row.label}-${row.text}`}>
            <dt>{row.label}</dt>
            <dd className="country-detail country-legal-name">{row.text}</dd>
          </div>
        ))}
        {showExplanation ? (
          <div>
            <dt>中文说明</dt>
            <dd className="country-detail">{result.chineseExplanation.text}</dd>
          </div>
        ) : null}
        <div>
          <dt>翻译状态</dt>
          <dd className="country-detail">{TRANSLATION_STATUS[result.chineseExplanation.status]}</dd>
        </div>
      </dl>

      <div className="country-rates" aria-label={`${meta.heading}税率`}>
        <div className="rate-heading-row">
          <h4>税率</h4>
        </div>

        {noChineseExportRate ? (
          <p className="rate-missing">未检索到中国出口关税</p>
        ) : null}

        {primaryRate ? (
          <div className={`rate-line rate-line-primary${primaryRate.kind === "text" ? " rate-line-text" : ""}`}>
            <span className="rate-line-label">{rateLabel(result, primaryRate, true)}</span>
            <strong>{primaryRate.displayValue}</strong>
            <span className="rate-line-status">{primaryRate.includedInConfirmedTotal ? "已确认基础税率" : "已确认基础税率 · 未计入"}</span>
          </div>
        ) : null}

        {ordinaryRates.map((rate, index) => {
          const status = rateStatus(rate);
          return (
            <div className={`rate-line${rate.kind === "text" ? " rate-line-text" : ""}`} key={rateLineKey(rate, index)}>
              <span className="rate-line-label">{rateLabel(result, rate)}</span>
              <strong>{rate.displayValue}</strong>
              {status ? <span className="rate-line-status">{status}</span> : null}
            </div>
          );
        })}

        {tradeRemedyRates.length > 0 ? (
          <section className="rate-remedy-group" role="group" aria-label="附加税与贸易救济">
            <h5>附加税与贸易救济</h5>
            {simaCodes.length > 0 ? <p className="rate-remedy-code">SIMA 候选税号：{simaCodes.join("、")}</p> : null}
            {tradeRemedyRates.length > 3 ? (
              <p className="rate-remedy-summary">已检索到 {tradeRemedyRates.length} 条贸易救济候选税率，需根据原产国、出口商或案件范围复核。</p>
            ) : null}
            <details className="rate-remedy-details" open={tradeRemedyRates.length <= 3}>
              <summary>{tradeRemedyRates.length > 3 ? `查看候选税率明细（${tradeRemedyRates.length} 条）` : "候选税率明细"}</summary>
              <div className="rate-remedy-lines">
                {tradeRemedyRates.map((rate, index) => {
                  const status = rateStatus(rate);
                  return (
                    <div className={`rate-line${rate.kind === "text" ? " rate-line-text" : ""}`} key={rateLineKey(rate, index)}>
                      <span className="rate-line-label">{rateLabel(result, rate)}</span>
                      <strong>{rate.displayValue}</strong>
                      {status ? <span className="rate-line-status">{status}</span> : null}
                    </div>
                  );
                })}
              </div>
            </details>
          </section>
        ) : null}

        <p className="confirmed-total">
          已确认税率合计：{result.confirmedTotalPercent === null ? "人工复核" : displayPercent(result.confirmedTotalPercent)}
        </p>
      </div>

      <div className="country-card-footer">
        <span className="country-detail">文件提示：{result.documents.length} 项；贸易措施：{result.measures.length} 项</span>
        <div className="country-card-actions">
          <button
            className="secondary-button"
            type="button"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={onExpand}
          >
            {expanded ? "收起提示" : "查看提示"}
          </button>
        </div>
      </div>
    </article>
  );
}
